import { EyeOff, Loader2, LogOut } from 'lucide-react';
import { useState } from 'react';
import { NavLink } from 'react-router';
import { useAuth } from '@/auth/AuthContext';
import { usePartnerById } from '@/hooks/usePartners';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { cn } from '@/lib/utils';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'tw:font-serif tw:text-lg tw:tracking-wide tw:transition-colors',
    isActive ? 'tw:text-ed-gold' : 'tw:text-white/90 tw:hover:text-ed-gold',
  );

/** Public-facing site header, styled in the editorial brand look. */
export function SiteHeader() {
  const { user, isAdmin, isPartner, partnerUser, signOutUser } = useAuth();
  const { settings } = useSiteSettings();
  // Who the signed-in person is here on behalf of, shown under their name. An
  // admin is one of ours whatever else they are, so their own partner — if they
  // happen to have one — is not what the header should claim they speak for.
  const { partner } = usePartnerById(isAdmin ? undefined : partnerUser?.partnerId);
  const orgName = isAdmin ? 'Eighty Days' : partner?.name;
  // Admins keep the link while the section is switched off, so the thing being
  // hidden stays one click away from the switch that hides it.
  const showTourLeaders = settings.tourLeadersVisible || isAdmin;
  // Signing out normally swaps this header out from under itself, so this only
  // ever shows on a slow or failing round trip — which is exactly when a second
  // click looks like the right idea.
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOutUser();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <header className='tw:sticky tw:top-0 tw:z-40 tw:bg-ed-black'>
      <div className='tw:mx-auto tw:flex tw:max-w-6xl tw:items-center tw:justify-between tw:px-6 tw:py-5'>
        <NavLink to='/' className='tw:flex tw:items-center tw:gap-3'>
          <img src='/eightydays_balloon.png' alt='Eighty Days' className='tw:h-10 tw:w-auto tw:object-contain' />
          <span className='tw:flex tw:flex-col tw:leading-none'>
            <span className='tw:font-serif tw:text-lg tw:tracking-wider tw:uppercase tw:text-white'>Eighty Days</span>
            <span className='tw:text-[0.6rem] tw:tracking-[0.35em] tw:uppercase tw:text-ed-gold/70'>
              Exclusive Experiences
            </span>
          </span>
        </NavLink>

        <nav className='tw:flex tw:items-center tw:gap-8'>
          <NavLink to='/catalog' className={navLinkClass}>
            Experiences
          </NavLink>
          {showTourLeaders && (
            <NavLink
              to='/tour-leaders'
              className={navLinkClass}
              title={settings.tourLeadersVisible ? undefined : 'Hidden from partners'}
            >
              Tour Leaders
              {!settings.tourLeadersVisible && <EyeOff className='tw:ml-1.5 tw:inline tw:size-3.5' />}
            </NavLink>
          )}
          {/* The partner area has no sidebar of its own — this top nav is how
              partners reach their pages. `end` on the profile link so it does
              not stay lit while its child route is open. */}
          {isPartner && (
            <>
              <NavLink to='/partner/requests' className={navLinkClass}>
                Requests
              </NavLink>
              <NavLink to='/partner' end className={navLinkClass}>
                Profile
              </NavLink>
            </>
          )}
          {user && (
            <div className='tw:flex tw:items-center tw:gap-4 tw:border-l tw:border-white/15 tw:pl-6'>
              <span className='tw:hidden tw:flex-col tw:text-right tw:leading-tight tw:sm:flex'>
                <span className='tw:text-xs tw:text-white/50' title={user.email ?? undefined}>
                  {user.displayName || user.email}
                </span>
                {orgName && (
                  <span className='tw:text-[0.6rem] tw:tracking-[0.2em] tw:uppercase tw:text-ed-gold/70'>
                    {orgName}
                  </span>
                )}
              </span>
              <button
                type='button'
                onClick={() => void handleSignOut()}
                disabled={signingOut}
                aria-busy={signingOut || undefined}
                title='Sign out'
                className='tw:flex tw:items-center tw:gap-1.5 tw:text-white/70 tw:transition-colors tw:hover:text-ed-gold tw:disabled:hover:text-white/70'
              >
                {signingOut ? (
                  <Loader2 className='tw:size-4 tw:animate-spin' />
                ) : (
                  <LogOut className='tw:size-4' />
                )}
                <span className='tw:hidden tw:text-sm tw:sm:inline'>Sign out</span>
              </button>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
}
